/**
 * GA4 (Google Analytics 4) analytics helper — flag-OFF by default, consent-gated.
 *
 * Contract (mirrors the conditional-provider pattern in
 * `contexts/RecaptchaContext.jsx`):
 *   - REACT_APP_GA4_ENABLED        must be the string "true" to enable at all.
 *   - REACT_APP_GA4_MEASUREMENT_ID is the GA4 Measurement ID (e.g. "G-XXXXXXXXXX").
 *     This is supplied by Gordon / the dev at deploy time — it is NEVER hardcoded.
 *
 * Hard rules baked in here:
 *   - When the flag is off OR the id is missing, EVERYTHING is a no-op and the
 *     gtag <script> is never injected (byte-safe default — zero third-party load).
 *   - The gtag script + GA `config` only run AFTER explicit user consent
 *     (see `initGa`, called by ConsentContext once consent === "granted").
 *   - Do-Not-Track is respected: if the browser sends DNT, GA never initializes
 *     and trackEvent is a no-op, regardless of consent.
 *
 * Everything is defensive (try/catch, typeof guards) so a missing/blocked
 * analytics layer can never break the host app.
 */

export const GA4_ENABLED = process.env.REACT_APP_GA4_ENABLED === "true";
export const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

const GTAG_SRC = "https://www.googletagmanager.com/gtag/js";

let gaInitialized = false;

// T22.245: the last page (path + query) a page_view was sent for, so the
// initial page_view initGa() sends and the PageViewTracker's first effect for
// the same location never double-count, whichever runs first.
let lastPageViewPath = null;

// minors-w2-fe-settings-age-dob-and-referral-gate (mission minors-13plus,
// plan ~/plans/minors-13plus-compliance-2026-09-14.md, B.17 "minor mode"):
// no analytics/profiling events for a known-minor age_band. Wired by
// components/MinorModeSync (mounted once in App.js, flag-gated) whenever the
// current user's age_band changes — kept as a plain setter, not a direct
// redux import, so this module stays dependency-free and independently
// testable exactly like today (see analytics.test.js's isolateModules idiom).
let minorModeActive = false;

export function setMinorModeActive(isMinor) {
  minorModeActive = Boolean(isMinor);
}

function isMinorModeActive() {
  return minorModeActive;
}

/**
 * Returns true when the user's browser has signalled Do-Not-Track.
 * Covers the historical spellings across browsers. Defensive: never throws.
 */
export function isDoNotTrackEnabled() {
  try {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return false;
    }
    const dnt =
      navigator.doNotTrack ||
      window.doNotTrack ||
      (navigator.msDoNotTrack !== undefined ? navigator.msDoNotTrack : null);
    return dnt === "1" || dnt === "yes" || dnt === true;
  } catch (e) {
    // If we can't read the signal, fail closed (treat as DNT-on = no tracking).
    return true;
  }
}

/**
 * Whether GA is configured at all (flag on + id present) AND the user has not
 * opted out via Do-Not-Track. This gates whether we ever even offer tracking.
 */
export function isGaConfigured() {
  return Boolean(GA4_ENABLED && GA4_MEASUREMENT_ID) && !isDoNotTrackEnabled();
}

/**
 * Lazily inject the gtag.js script and initialize GA4.
 * MUST only be called after explicit consent (ConsentContext enforces this).
 * Idempotent + fully guarded — safe to call more than once.
 */
export function initGa() {
  if (!isGaConfigured()) {
    return false;
  }
  if (gaInitialized) {
    return true;
  }
  try {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return false;
    }

    // dataLayer + gtag shim (the canonical GA4 bootstrap).
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    }
    window.gtag = window.gtag || gtag;

    // Inject the loader script once.
    const existing = document.querySelector(
      `script[data-iyuga-ga4="${GA4_MEASUREMENT_ID}"]`
    );
    if (!existing) {
      const script = document.createElement("script");
      script.async = true;
      script.src = `${GTAG_SRC}?id=${encodeURIComponent(GA4_MEASUREMENT_ID)}`;
      script.setAttribute("data-iyuga-ga4", GA4_MEASUREMENT_ID);
      document.head.appendChild(script);
    }

    window.gtag("js", new Date());
    window.gtag("config", GA4_MEASUREMENT_ID, {
      // SPA: we send page_view manually on route changes via trackPageView,
      // so disable the automatic one to avoid double-counting.
      send_page_view: false,
      anonymize_ip: true,
    });

    gaInitialized = true;
    // T22.245: with send_page_view:false nothing records the page the user is
    // on when GA starts (fresh load with stored consent, or Accept clicked
    // mid-session). Send it here; later route changes come from PageViewTracker.
    trackPageView();
    return true;
  } catch (e) {
    // Never let analytics break the app.
    return false;
  }
}

/**
 * Tear GA back down on consent withdrawal: stop further hits by setting the
 * standard GA opt-out global. We cannot un-inject the script, but this flag is
 * the documented way to make gtag a no-op for this measurement id.
 */
export function disableGa() {
  try {
    if (typeof window !== "undefined" && GA4_MEASUREMENT_ID) {
      window[`ga-disable-${GA4_MEASUREMENT_ID}`] = true;
    }
    gaInitialized = false;
    lastPageViewPath = null;
  } catch (e) {
    /* no-op */
  }
}

/**
 * Thin event tracker. Call from anywhere:  trackEvent("sign_up", { method }).
 * No-op unless GA is configured, consented, and initialized. Never throws.
 */
export function trackEvent(name, params = {}) {
  try {
    if (!name || !isGaConfigured() || !gaInitialized || isMinorModeActive()) {
      return;
    }
    if (typeof window === "undefined" || typeof window.gtag !== "function") {
      return;
    }
    window.gtag("event", name, params || {});
  } catch (e) {
    /* no-op */
  }
}

/**
 * SPA page-view helper (manual, since send_page_view is disabled above).
 * Called by initGa (the landing page) and components/PageViewTracker (every
 * route change). `path` defaults to the current pathname + search. A repeat
 * call for the page already sent is skipped, so one page = one page_view.
 * No-op unless configured + consented + initialized.
 */
export function trackPageView(path) {
  try {
    if (!isGaConfigured() || !gaInitialized || isMinorModeActive()) {
      return;
    }
    if (typeof window === "undefined" || typeof window.gtag !== "function") {
      return;
    }
    const loc = window.location;
    const pagePath = path || (loc ? `${loc.pathname}${loc.search}` : undefined);
    if (!pagePath || pagePath === lastPageViewPath) {
      return;
    }
    lastPageViewPath = pagePath;
    window.gtag("event", "page_view", {
      page_path: pagePath,
      page_location: loc ? `${loc.origin}${pagePath}` : undefined,
      page_title: typeof document !== "undefined" ? document.title : undefined,
    });
  } catch (e) {
    /* no-op */
  }
}

/** Test/diagnostic hook — exposes minor-mode state without leaking module internals. */
export function _isMinorModeActive() {
  return minorModeActive;
}

/** Test/diagnostic hook — exposes init state without leaking module internals. */
export function _isGaInitialized() {
  return gaInitialized;
}
